import { Observable } from 'rxjs/Rx'

import {
  Store
} from '../'
import {
  AppContext,
  HttpHelper
} from '../../../helpers'
import { BaseModel } from '../../../models'
import { Uri } from '../../../core/models'
import { ModelMapper } from '../model-mapper'
import { BackendStrategy } from '../backend-strategy'
import { PresenterFactory } from './presenters'
import { JsonapiUrlGenerator } from './jsonapi-url-generator'

export class JsonApiBackend extends BackendStrategy<BaseModel> {

  constructor(
    private _httpHelper: HttpHelper,
    _appContext: AppContext,
    private _store: Store,
    private _presenterFactory: PresenterFactory,
    private _jsonapiUrlGenerator: JsonapiUrlGenerator,
    private _modelMapper: ModelMapper
  ) {
    super()
  }

  // TODO [GitHub Issue]: Create specific request classes DeleteRequest, FindRequest. These classes would handle the creation of
  // the url method and json. You can then have a factory that would be used here.

  public delete(models: BaseModel[]): Observable<any> {
    let url
    let json
    let method
    const firstModel = models[0]

    if (models.length <= 1) {
      url = this._jsonapiUrlGenerator.generateUrl(firstModel.type, firstModel.id)
      method = 'delete'
    } else {
      // TODO [GitHub Issue]: Should we return a Uri object from the generator?
      // TODO [GitHub Issue]: Should the generator be extended to take a custom suffix?
      url = new Uri(this._jsonapiUrlGenerator.generateUrl(firstModel.type))
      url.addPathPart('bulk_delete')
      url = url.toString()
      json = this._generateBulkActionJson(models)
      method = 'post'
    }
    return this._processRequest(method, url, json)
  }

  public findById(type: string, id: string, includes: string[] = []): Observable<BaseModel> {
    const url = this._jsonapiUrlGenerator.generateUrl(type, id, includes)
    return this._processRequest('get', url) as Observable<BaseModel>
  }

  public findBy(type: string, searchCriteria: any, includes: string[] = []): Observable<BaseModel[]> {
    const url = this._jsonapiUrlGenerator.generateUrl(type, searchCriteria, includes)
    return this._processRequest('get', url) as Observable<BaseModel[]>
  }

  public save(models: BaseModel[]): Observable<BaseModel[]> {
    return models.length <= 1 ?
      this._singleModelSaveStrategy(models[0]) :
      this._bulkSaveStrategy(models)
  }

  private _bulkSaveStrategy(models: BaseModel[]) {
    const postMap = new Map<number, BaseModel>()
    const putMap = new Map<number, BaseModel>()
    const orderedModels = []
    let requestMaps = [postMap, putMap]
    let requests: Array<Observable<any>>
    let json
    let method
    let url
    let firstModel

    models.forEach((model, index) => {
      model.id ?
        putMap.set(index, model) :
        postMap.set(index, model)
    })

    requestMaps = requestMaps.filter((requestMap) => requestMap.size)

    requests = requestMaps
      .map((requestMap) => {
        const requestArray = Array.from(requestMap.values())

        firstModel = requestArray[0]
        json = this._generateBulkActionJson(requestArray)
        method = this._getModelSaveMethod(firstModel)
        url = this._jsonapiUrlGenerator.generateUrl(firstModel.type)
        return this._processRequest(method, url, json)
      })

    return Observable.forkJoin(...requests)
      .map((results) => {
        requestMaps.forEach((requestMap, requestIndex) => {
          // If there is only one result in a request, the server may return single objects
          // as opposed to an array with a single element; we take care of that here
          results[requestIndex] = Array.isArray(results[requestIndex]) ?
            results[requestIndex] :
            [ results[requestIndex] ]

          Array.from(requestMap.keys()).forEach((orderedIndex, index) => {
            orderedModels[orderedIndex] = results[requestIndex][index]
          })
        })
        return orderedModels
      })
  }

  private _generateBulkActionJson(models: BaseModel[]) {
    const json = {data: []}

    models.forEach((model) => {
      const requestBody = this._generateSingleSaveJson(model)

      json['data'].push(requestBody.data)
    })

    return json
  }

  private _singleModelSaveStrategy(model: BaseModel) {
    const json = this._generateSingleSaveJson(model)
    const method = this._getModelSaveMethod(model)
    const url = this._jsonapiUrlGenerator.generateUrl(model.type, model.id)
    return this._processRequest(method, url, json).map((singleModel) => [singleModel])
  }

  private _getModelSaveMethod(model: BaseModel): string {
    return model.id ? 'put' : 'post'
  }

  private _generateSingleSaveJson(model): {data: any, included: any} {
    const presenter = this._presenterFactory.get(model.type)
    const requestBody = presenter.render(this._modelMapper.mapToJson(model))

    if (requestBody.data.id === 'undefined') {
      delete requestBody.data.id
    }
    return requestBody
  }

  private _processRequest(method: string, url: string, data?: {}): Observable<BaseModel | BaseModel[]> {
    return this._httpHelper[method](url, data)
      .map((response) => {
        response = this._store.sync(response) || {}
        // TODO [GitHub Issue]: It would be better to have a custom class for arrays that would respond to type
        const type = Array.isArray(response) ? (response[0] || {}).type : response.type
        return type ? this._modelMapper.mapToModel(type, response) : response
      })
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/json-api-backend/json-api.backend.ts